import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCookieConsent } from "../../contexts/CookieConsentContext";

const COOKIE_KEY = "cookieConsent";
const CONSENT_DURATION_MS = 180 * 24 * 60 * 60 * 1000;
const GTM_ID = "GTM-T7MMFR8X"; // Replace with your GTM ID

const defaultConsent = {
  necessary: true,
  analytics: false,
  marketing: false,
  preferences: false,
};

const loadGTM = (gtmId, consent) => {
  if (window.gtmDidInit) return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "default_consent",
    analytics_storage: consent.analytics ? "granted" : "denied",
    ad_storage: consent.marketing ? "granted" : "denied",
    preferences_storage: consent.preferences ? "granted" : "denied",
  });

  const script = document.createElement("script");
  script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
  script.async = true;
  document.head.appendChild(script);

  window.gtmDidInit = true;
};

const CookieConsentOffcanvas = ({ onClose }) => {
  const { t } = useTranslation();
  const { forceOpen, setForceOpen } = useCookieConsent();
  const [consent, setConsent] = useState(defaultConsent);
  const [show, setShow] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const addBackdrop = () => {
    if (!document.getElementById("cookie-backdrop")) {
      const backdrop = document.createElement("div");
      backdrop.className = "offcanvas-backdrop fade show";
      backdrop.id = "cookie-backdrop";
      Object.assign(backdrop.style, {
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: 1040,
      });
      document.body.appendChild(backdrop);
      document.body.style.overflow = "hidden";
    }
  };

  const removeBackdrop = () => {
    const el = document.getElementById("cookie-backdrop");
    if (el) el.remove();
    document.body.style.overflow = "";
  };

  useEffect(() => {
    if (forceOpen) {
      setShow(true);
      addBackdrop();
      return;
    }

    const stored = localStorage.getItem(COOKIE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        const expired = Date.now() - new Date(parsed.savedAt).getTime() > CONSENT_DURATION_MS;
        if (!expired) {
          setConsent(parsed.consent);
          if (parsed.consent.analytics || parsed.consent.marketing) {
            loadGTM(GTM_ID, parsed.consent);
          }
          return;
        }
      } catch {
        console.warn("Malformed cookie data");
      }
    }

    setShow(true);
    addBackdrop();
  }, [forceOpen]);

  const saveConsent = (updated) => {
    localStorage.setItem(
      COOKIE_KEY,
      JSON.stringify({ consent: updated, savedAt: new Date().toISOString() })
    );
  };

  const closeConsent = () => {
    setShow(false);
    removeBackdrop();
    setForceOpen(false);
    onClose?.();
  };

  const handleSave = () => {
    saveConsent(consent);
    if (consent.analytics || consent.marketing) {
      loadGTM(GTM_ID, consent);
    }
    closeConsent();
  };

  const handleAcceptAll = () => {
    const updated = {
      ...defaultConsent,
      analytics: true,
      marketing: true,
      preferences: true,
    };
    setConsent(updated);
    saveConsent(updated);
    loadGTM(GTM_ID, updated);
    closeConsent();
  };

  const handleRejectAll = () => {
    const updated = {
      ...defaultConsent,
      analytics: false,
      marketing: false,
      preferences: false,
    };
    setConsent(updated);
    saveConsent(updated);
    closeConsent();
  };

  if (!show) return null;

  return (
    <div
	  className="offcanvas offcanvas-bottom show bg-white shadow d-flex flex-column w-auto"
	  tabIndex="-1"
	  id="offcanvasCookieConsent"
	  aria-labelledby="offcanvasCookieConsentLabel"
	  style={{
		visibility: "visible",
		zIndex: 1050,
		height: "auto", // allow height to follow content
		maxHeight: "100vh", // prevent overflow
		overflowY: "auto", // scroll if needed
	  }}
	>
      <div className="offcanvas-body flex-grow-1">
        

        {editMode && (
          <div className="widget mb-4">
            <span className="d-flex eyebrow text-muted mb-2">{t("cookies.categories", "Categories")}</span>
            <div>
              {['necessary', 'analytics', 'marketing', 'preferences'].map((id) => (
                <div key={id} className="form-check form-check-inline">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={id}
                    checked={consent[id]}
                    disabled={id === "necessary"}
                    onChange={(e) =>
                      setConsent({ ...consent, [id]: e.target.checked })
                    }
                  />
                  <label className="form-check-label" htmlFor={id}>
                    {t(`cookies.${id}`)}{id === "necessary" ? ` (${t("cookies.alwaysOn", "Always On")})` : ""}
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}

        {!editMode ? (
          <div className="row g-2">
			  <div className="col-12 col-md-6">
				<p>{t("cookies.message", "This website uses cookies to improve your experience and analyze traffic. Click \"Accept All\" to consent. You can manage preferences at any time.")}</p>
			  </div>
			  <div className="col-12 col-md-3">
				<button
				  className="btn btn-sm btn-outline-dark w-100"
				  onClick={() => setEditMode(true)}
				>
				  {t("cookies.edit", "Edit preferences")}
				</button>
			  </div>
			  <div className="col-12 col-md-3">
				<button className="btn btn-sm btn-dark w-100" onClick={handleAcceptAll}>
				  {t("cookies.acceptAll", "Accept all")}
				</button>
			  </div>
			</div>
        ) : (
          <div className="row g-2">
            <div className="col-12 col-md-6">
              <button className="btn btn-sm btn-dark w-100" onClick={handleSave}>
                {t("cookies.save", "Save Preferences")}
              </button>
            </div>
            <div className="col-12 col-md-6">
              <button
                className="btn btn-sm btn-outline-dark w-100"
                onClick={() => setEditMode(false)}
              >
                {t("cookies.cancel", "Cancel")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieConsentOffcanvas;
